import {
  BodyText,
  Button,
  Input,
  LinkText,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@postscript/components';
import FormikField from 'components/billing/common/FormikField';
import FormikFieldSelectMenu from 'components/billing/common/FormikFieldSelectMenu';
import {
  useGetComplianceContactInfo,
  useSetComplianceContactInfo,
} from 'components/compliance/useCompliance';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import {
  COUNTRY_OPTIONS,
  SUPPORTED_COUNTRY_REGIONS,
} from 'components/onboarding/steps/constants';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useResubmitTFNVerification } from '../hooks/useTFNVerification';

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  margin-top: var(--spacing-3);
`;

const AddressRow = styled.div`
  display: flex;
  gap: var(--spacing-3);

  & > * {
    flex: 1;
  }
`;

const TextSection = styled.div`
  margin-bottom: var(--spacing-3);
`;

const HelperText = styled(BodyText)`
  margin-top: var(--spacing-1);
`;

const BulletList = styled.ul`
  padding-left: var(--spacing-4);
`;

interface FormValues {
  businessAddress: {
    address1: string;
    address2: string;
    city: string;
    countryCode: string;
    provinceCode: string;
    zip: string;
  };
}

const getValidationSchema = () =>
  Yup.object({
    businessAddress: Yup.object({
      address1: Yup.string().required('Enter an address').nullable(),
      address2: Yup.string().nullable(),
      city: Yup.string().required('Enter a city').nullable(),
      countryCode: Yup.string().max(2).required('Select a country').nullable(),
      provinceCode: Yup.string()
        .when('countryCode', (cc: string, s: Yup.StringSchema) => {
          const hasRegions = Boolean(SUPPORTED_COUNTRY_REGIONS[cc]);
          return hasRegions
            ? s
                .required('Select a state/province')
                .max(3, 'Enter a two- or three-character code')
            : s.max(64);
        })
        .nullable(),
      zip: Yup.string().required('Enter a zip or postal code').nullable(),
    }),
  });

interface InvalidBusinessAddressModalProps {
  title?: string;
}

export default function InvalidBusinessAddressModal({
  title = 'Toll-Free Number verification denied',
}: InvalidBusinessAddressModalProps): JSX.Element {
  const { hideModal } = useGlobalModal();
  const { data: complianceContactInfo } = useGetComplianceContactInfo();
  const { mutateAsync: setComplianceContactInfo } =
    useSetComplianceContactInfo();
  const { mutateAsync: resubmitTFNVerification } = useResubmitTFNVerification();

  const { billingAddress } = complianceContactInfo ?? {};

  const initialValues: FormValues = {
    businessAddress: {
      address1: billingAddress?.address1 ?? '',
      address2: billingAddress?.address2 ?? '',
      city: billingAddress?.city ?? '',
      zip: billingAddress?.zip ?? '',
      countryCode: billingAddress?.countryCode ?? 'US',
      provinceCode: billingAddress?.provinceCode ?? '',
    },
  };

  const handleSubmit = async (values: FormValues) => {
    if (!complianceContactInfo) {
      return;
    }

    const { businessAddress } = values;

    await setComplianceContactInfo({
      ...complianceContactInfo,
      billingAddress: businessAddress,
    });

    await resubmitTFNVerification();
    hideModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
      enableReinitialize
    >
      {({ values, isSubmitting }) => (
        <Form>
          <ModalHeader onCancel={hideModal}>{title}</ModalHeader>
          <ModalBody>
            <TextSection>
              <BodyText bold size="small" color="var(--text-color)">
                What&apos;s wrong?
              </BodyText>
              <BodyText size="small" color="var(--text-color)">
                Business address is incomplete. Carriers require a complete
                address, including street address, city, state/province/region,
                and postal code.
              </BodyText>
            </TextSection>

            <TextSection>
              <BodyText bold size="small" color="var(--text-color)">
                Ensure that your business address...
              </BodyText>
              <BulletList>
                <li>
                  <BodyText size="small" color="var(--text-color)">
                    Contains correct street address, city,
                    state/province/region, and postal code
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small" color="var(--text-color)">
                    Includes suite or unit, if applicable
                  </BodyText>
                </li>
                <li>
                  <BodyText size="small" color="var(--text-color)">
                    Matches official business records
                  </BodyText>
                </li>
              </BulletList>
            </TextSection>

            <TextSection>
              <BodyText size="small" color="var(--text-color)">
                <strong>
                  Please update your business address, then resubmit.
                </strong>{' '}
                Verification typically takes up to 5 business days. Your website
                must remain live during this time.
              </BodyText>
            </TextSection>

            <FormSection>
              <FormikFieldSelectMenu
                name="businessAddress.countryCode"
                label="Country/region"
                placeholder="Select a country"
                options={COUNTRY_OPTIONS}
                required
              />
              <FormikField
                name="businessAddress.address1"
                label="Address line 1"
                type="text"
                as={Input}
                placeholder="Street address"
                fieldSize="medium"
                required
              />
              <FormikField
                name="businessAddress.address2"
                label="Address line 2 (optional)"
                type="text"
                as={Input}
                placeholder="Apartment, suite, etc."
                fieldSize="medium"
              />
              <AddressRow>
                <FormikField
                  name="businessAddress.city"
                  label="City"
                  placeholder="Enter city"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
                {SUPPORTED_COUNTRY_REGIONS[
                  values.businessAddress.countryCode
                ] ? (
                  <FormikFieldSelectMenu
                    name="businessAddress.provinceCode"
                    label="State/province"
                    options={
                      SUPPORTED_COUNTRY_REGIONS[
                        values.businessAddress.countryCode
                      ]
                    }
                    required
                    openMenuMaxVisibleOptions={2}
                  />
                ) : (
                  <FormikField
                    name="businessAddress.provinceCode"
                    label="State/province"
                    placeholder="State/Province"
                    type="text"
                    as={Input}
                    fieldSize="medium"
                  />
                )}
                <FormikField
                  name="businessAddress.zip"
                  label="Postal code"
                  placeholder="Postal code"
                  type="text"
                  as={Input}
                  required
                  fieldSize="medium"
                />
              </AddressRow>
            </FormSection>
            <HelperText size="x-small" color="var(--gray-5)">
              You can also find these fields under{' '}
              <LinkText
                href="/account/settings/compliance#billing-address"
                onClick={hideModal}
              >
                Shop settings → Compliance → Billing address
              </LinkText>
            </HelperText>
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !complianceContactInfo}
            >
              Save and Resubmit
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
}
